<template>
  <div class="splash-page">
    <!-- Header -->
    <AppHeader
      :logo-src="logoSrc"
      :dark-logo-src="darkLogoSrc"
      :user-name="userName"
      :user-menu-items="userMenuItems"
      :show-product-switcher="false"
      @logout="$emit('logout')"
      @user-menu-click="$emit('user-menu-click', $event)"
      @theme-change="$emit('theme-change', $event)"
    />

    <!-- Main Content -->
    <main class="splash-content">
      <div class="splash-container">
        <!-- Loading State -->
        <div v-if="loadingPortalId" class="loading-state">
          <div class="loading-spinner">
            <LucideIcon name="Loader2" class="spinner-icon" />
          </div>
          <h2 class="loading-title">
            Redirecting to {{ getPortalTitle(loadingPortalId) }}...
          </h2>
          <p class="loading-message">Please wait while we securely connect you</p>
        </div>

        <!-- Error State -->
        <div v-else-if="errorPortalId" class="error-state">
          <div class="error-icon">
            <LucideIcon name="AlertCircle" />
          </div>
          <h2 class="error-title">Connection Failed</h2>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="handleRetry">
            <LucideIcon name="RefreshCw" class="retry-icon" />
            Retry Connection
          </button>
          <button class="back-button" @click="clearError">
            <LucideIcon name="ArrowLeft" class="back-icon" />
            Back to Portals
          </button>
        </div>

        <!-- Normal State: Portal Selection -->
        <div v-else class="portal-selection">
          <!-- Welcome Header -->
          <div class="welcome-header">
            <h1 class="welcome-title">Welcome {{ userName }}</h1>
            <p class="welcome-subtitle">
              Select the Syrenis product you'd like to access
            </p>
          </div>

          <!-- Portal Grid -->
          <div class="portal-grid">
            <PortalCard
              v-for="portal in portals"
              :key="portal.id"
              :id="portal.id"
              :title="portal.title"
              :description="portal.description"
              :icon="portal.icon"
              :disabled="portal.disabled"
              :loading="false"
              @click="handlePortalClick(portal)"
            />
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="splash-footer">
      <p class="footer-text">
        © {{ currentYear }} Syrenis. All rights reserved.
      </p>
    </footer>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import AppHeader from './AppHeader.vue';
import PortalCard from './PortalCard.vue';
import LucideIcon from '../LucideIcon';

export interface Portal {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  disabled?: boolean;
}

export interface UserMenuItem {
  label: string;
  icon?: string;
  action?: string;
  divider?: boolean;
}

export default Vue.extend({
  name: 'SplashPage',

  components: {
    AppHeader,
    PortalCard,
    LucideIcon,
  },

  props: {
    logoSrc: {
      type: String,
      required: true,
    },
    darkLogoSrc: {
      type: String,
      required: true,
    },
    userName: {
      type: String,
      default: 'User',
    },
    userMenuItems: {
      type: Array as () => UserMenuItem[],
      default: () => [],
    },
    portals: {
      type: Array as () => Portal[],
      required: true,
    },
    loadingPortalId: {
      type: String,
      default: null,
    },
    errorPortalId: {
      type: String,
      default: null,
    },
    errorMessage: {
      type: String,
      default: "We couldn't complete the secure handoff. Please retry.",
    },
  },

  computed: {
    currentYear(): number {
      return new Date().getFullYear();
    }
  },

  methods: {
    getPortalTitle(portalId: string): string {
      const portal = this.portals.find((p: Portal) => p.id === portalId);
      return portal?.title || 'Portal';
    },

    handlePortalClick(portal: Portal): void {
      this.$emit('portal-click', portal);
    },

    handleRetry(): void {
      if (this.errorPortalId) {
        this.$emit('retry-click', this.errorPortalId);
      }
    },

    clearError(): void {
      this.$emit('retry-click', null);
    }
  }
});
</script>

<style scoped>
/* === Root Container === */
.splash-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--background, #ffffff);
  color: var(--foreground, #1a1a1a);
}

/* === Main Content === */
.splash-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
}

.splash-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

/* === Welcome Header === */
.portal-selection {
  animation: fadeIn 0.3s ease-in;
}

.welcome-header {
  text-align: center;
  margin-bottom: 3rem;
}

.welcome-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
  color: var(--foreground, #1a1a1a);
}

.welcome-subtitle {
  font-size: 1.125rem;
  color: var(--muted-foreground, #666666);
  margin: 0;
}

/* === Portal Grid === */
.portal-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;
}

/* Responsive Grid */
@media (max-width: 1024px) {
  .portal-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .portal-grid {
    grid-template-columns: 1fr;
  }

  .welcome-title {
    font-size: 2rem;
  }

  .welcome-subtitle {
    font-size: 1rem;
  }
}

/* === Loading State === */
.loading-state {
  text-align: center;
  padding: 4rem 2rem;
  animation: fadeIn 0.3s ease-in;
}

.loading-spinner {
  margin-bottom: 2rem;
}

.spinner-icon {
  width: 4rem;
  height: 4rem;
  color: var(--primary, #006ce5);
  animation: spin 1s linear infinite;
}

.loading-title {
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 0.75rem;
  color: var(--foreground, #1a1a1a);
}

.loading-message {
  font-size: 1rem;
  color: var(--muted-foreground, #666666);
  margin: 0;
}

/* === Error State === */
.error-state {
  text-align: center;
  padding: 4rem 2rem;
  max-width: 500px;
  margin: 0 auto;
  animation: fadeIn 0.3s ease-in;
}

.error-icon {
  margin-bottom: 1.5rem;
  color: var(--destructive, #dc2626);
}

.error-icon svg {
  width: 4rem;
  height: 4rem;
}

.error-title {
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 0.75rem;
  color: var(--foreground, #1a1a1a);
}

.error-message {
  font-size: 1rem;
  color: var(--muted-foreground, #666666);
  margin: 0 0 2rem;
  line-height: 1.5;
}

/* === Buttons === */
.retry-button,
.back-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius, 8px);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  min-width: 180px;
}

.retry-button {
  background: var(--primary, #006ce5);
  color: var(--primary-foreground, #ffffff);
  margin-bottom: 0.75rem;
}

.retry-button:hover:not(:disabled) {
  background: var(--primary-hover, #0056b3);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 108, 229, 0.2);
}

.retry-button:active:not(:disabled) {
  transform: translateY(0);
}

.back-button {
  background: transparent;
  color: var(--foreground, #1a1a1a);
  border: 1px solid var(--border, #e5e5e5);
}

.back-button:hover:not(:disabled) {
  background: var(--accent, #f5f5f5);
  border-color: var(--border-hover, #d4d4d4);
}

.retry-icon,
.back-icon {
  width: 1.25rem;
  height: 1.25rem;
}

/* === Footer === */
.splash-footer {
  padding: 2rem 1rem;
  text-align: center;
  border-top: 1px solid var(--border, #e5e5e5);
}

.footer-text {
  margin: 0;
  font-size: 0.875rem;
  color: var(--muted-foreground, #666666);
}

/* === Animations === */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* === Dark Mode Support === */
@media (prefers-color-scheme: dark) {
  .splash-page {
    background: var(--background, #0a0a0a);
    color: var(--foreground, #fafafa);
  }

  .welcome-title {
    color: var(--foreground, #fafafa);
  }

  .welcome-subtitle {
    color: var(--muted-foreground, #a3a3a3);
  }

  .loading-title,
  .error-title {
    color: var(--foreground, #fafafa);
  }

  .loading-message,
  .error-message {
    color: var(--muted-foreground, #a3a3a3);
  }

  .back-button {
    color: var(--foreground, #fafafa);
    border-color: var(--border, #333333);
  }

  .back-button:hover:not(:disabled) {
    background: var(--accent, #1a1a1a);
    border-color: var(--border-hover, #404040);
  }

  .splash-footer {
    border-top-color: var(--border, #333333);
  }

  .footer-text {
    color: var(--muted-foreground, #a3a3a3);
  }
}
</style>
